import React, { useState, useRef, useEffect } from "react";
import {
  ArrowRight,
  Globe,
  Leaf,
  Award,
  ShieldCheck,
  Menu,
  X,
  ChevronDown,
  Mail,
  Phone,
  MapPin,
  Send,
  CheckCircle,
  Linkedin,
} from "lucide-react";
// Preserving your image paths
import logo from "../../assets/top.webp";
import SGA from "../../assets/spice grains agro new.webp";
import Coconut from "../../assets/ceylon apparel house.webp";
import Como from "../../assets/spice grain commodities new gem.webp";
import { COMPANIES } from "../../data/companies";
import Avinash from "../../assets/avinash.webp";
import bg from "../../assets/header.webp";
import Nalliah from "../../assets/Nalliah.webp";
import crown from "../../assets/crown.webp";

// Founders Data
const founders = [
  {
    name: "Nalliah Devarajan",
    title: "Founding Chairman",
    description: "With over 34 years of experience in global trade, he established the foundation of D&D Lanka with a vision to bring Sri Lankan excellence to the world stage.",
    linkedin: "https://www.linkedin.com/in/nalliah-devarajan-82268053/",
    image: Nalliah,
  },
  {
    name: "Avinash Devarajan",
    title: "Managing Director",
    description: "Driving the second generation of leadership, Avinash focuses on modernizing trade strategies and expanding the group's global digital and sustainable footprint.",
    linkedin: "https://lk.linkedin.com/in/avinash-devarajan-510392307",
    image: Avinash,
  },
]

const PortalPage = ({ onSelectCompany }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [companiesDropdownOpen, setCompaniesDropdownOpen] = useState(false);
  const [formStatus, setFormStatus] = useState("idle");
  const dropdownRef = useRef(null);

  const topCompany =
    COMPANIES.find((c) => c.name.includes("Agro")) || COMPANIES[0];
  const bottomCompanies = COMPANIES.filter((c) => c.id !== topCompany.id);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setCompaniesDropdownOpen(false);
      }
    };

    if (companiesDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [companiesDropdownOpen]);

  // Close dropdowns when window resizes (mobile to desktop transition)
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
      setCompaniesDropdownOpen(false);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setFormStatus("submitting");
    setTimeout(() => setFormStatus("success"), 1500);
  };

  const getCompanyIcon = (iconName, size = 112) => {
    const icons = {
      leaf: (
        <img src={SGA} alt="Leaf Icon" className="w-full h-full object-cover" />
      ),
      shirt: (
        <img
          src={Coconut}
          alt="Shirt Icon"
          className="w-full h-full object-cover"
        />
      ),
      global: (
        <img
          src={Como}
          alt="Global Icon"
          className="w-full h-full object-cover"
        />
      ),
    };

    return (
      <div className="w-full h-full">
        {icons[iconName] || (
          <img
            src={SGA}
            alt="Default Icon"
            className="w-full h-full object-cover"
          />
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#020617] text-slate-100 selection:bg-amber-500/30">
      {/* Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#020617]/95 backdrop-blur-md border-b border-amber-900/20">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-900 to-slate-900 border border-amber-600/30 p-2 flex items-center justify-center">
                <img
                  src={crown}
                  alt="D&D Lanka"
                  className="w-20 h-20 object-cover mb-3"
                />
              </div>
              <span className="text-xl font-bold">
                <span className="text-slate-100">D & D</span>
                <span className="text-amber-500"> LANKA</span>
                <span className="block text-xs md:text-xs tracking-[0.02em] font-small text-amber-500/60 mt-0.5">
                  GROUP OF COMPANIES
                </span>
              </span>

            </div>

            <div className="hidden md:flex items-center gap-8">
              <a
                href="#home"
                className="text-slate-300 hover:text-amber-500 transition-colors font-medium"
              >
                Home
              </a>
              <a
                href="#about"
                className="text-slate-300 hover:text-amber-500 transition-colors font-medium"
              >
                About Us
              </a>

              <div
                ref={dropdownRef}
                className="relative"
                onMouseEnter={() => setCompaniesDropdownOpen(true)}
                onMouseLeave={() => setCompaniesDropdownOpen(false)}
              >
                {/* Trigger Link */}
                <a
                  href="#companies"
                  className="text-slate-300 hover:text-amber-500 transition-colors font-medium flex items-center gap-1 py-2"
                  aria-expanded={companiesDropdownOpen}
                  aria-haspopup="true"
                >
                  Companies
                  <ChevronDown
                    size={16}
                    className={`transition-transform duration-300 ${companiesDropdownOpen ? "rotate-180" : ""}`}
                  />
                </a>

                {/* Dropdown Menu */}
                {companiesDropdownOpen && (
                  /* This outer div acts as the bridge. pt-2 replaces mt-2 to remove the gap */
                  <div className="absolute top-full left-0 pt-2 w-64 z-50">
                    <div className="bg-blue-950/95 backdrop-blur-md border border-amber-900/20 rounded-lg shadow-2xl overflow-hidden">
                      {COMPANIES.map((company) => (
                        <button
                          key={company.id}
                          onClick={() => {
                            onSelectCompany(company);
                            setCompaniesDropdownOpen(false);
                          }}
                          className="w-full text-left px-4 py-3 text-slate-300 hover:bg-amber-500/10 hover:text-amber-500 transition-colors border-b border-amber-900/10 last:border-b-0"
                        >
                          {company.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <a
                href="#contact"
                className="text-slate-300 hover:text-amber-500 transition-colors font-medium"
              >
                Contact Us
              </a>
            </div>

            <button
              onClick={() => {
                setMobileMenuOpen(!mobileMenuOpen);
                setCompaniesDropdownOpen(false);
              }}
              className="md:hidden text-slate-300 hover:text-amber-500 transition-colors"
              aria-label="Toggle menu"
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-amber-900/20 pt-4">
              <div className="flex flex-col gap-4">
                <a
                  href="#home"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-slate-300 hover:text-amber-500 transition-colors font-medium"
                >
                  Home
                </a>
                <a
                  href="#about"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-slate-300 hover:text-amber-500 transition-colors font-medium"
                >
                  About Us
                </a>
                <div>
                  <a
                    href="#companies"
                    onClick={() => {
                      setCompaniesDropdownOpen(!companiesDropdownOpen);
                      setMobileMenuOpen(false);
                    }}
                    className="text-slate-300 hover:text-amber-500 transition-colors font-medium flex items-center gap-1 w-full"
                    aria-expanded={companiesDropdownOpen}
                  >
                    Companies
                    <ChevronDown
                      size={16}
                      className={`transition-transform duration-300 ${companiesDropdownOpen ? "rotate-180" : ""}`}
                    />
                  </a>
                  {companiesDropdownOpen && (
                    <div className="ml-4 mt-2 flex flex-col gap-2">
                      {COMPANIES.map((company) => (
                        <a
                          href="#companies"
                          key={company.id}
                          onClick={() => {
                            onSelectCompany(company);
                            setMobileMenuOpen(false);
                            setCompaniesDropdownOpen(false);
                          }}
                          className="text-left text-slate-400 hover:text-amber-500 transition-colors text-sm"
                        >
                          {company.name}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
                <a
                  href="#contact"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-slate-300 hover:text-amber-500 transition-colors font-medium"
                >
                  Contact Us
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Header */}
      <header
        id="home"
        className="relative py-32 px-6 text-center overflow-hidden border-b border-amber-900/20 mt-20"
      >
        {/* Background Image with Overlay */}
        <div
          className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${bg})`,
          }}
        />
        {/* Premium Overlays */}
        <div className="absolute inset-0 z-10 bg-slate-950/70 backdrop-blur-[2px]" />
        <div className="absolute inset-0 z-10 bg-gradient-to-b from-blue-950/40 via-transparent to-[#020617]" />
        <div className="absolute inset-0 z-10 bg-[radial-gradient(circle_at_50%_50%,rgba(245,158,11,0.1),transparent)]" />

        <div className="relative z-10 max-w-7xl mx-auto flex flex-col items-center justify-center">
          <div className="w-80 h-80 md:w-[4rem] md:h-[4rem] lg:w-[20rem] lg:h-[20rem] mx-auto mb-12 shadow-[0_0_100px_rgba(245,158,11,0.25)] rounded-full border border-amber-600/20 bg-gradient-to-b from-blue-950/5 to-[#020617]/40 p-5 md:p-10 lg:p-5 backdrop-blur-3xl flex items-center justify-center">
            <img
              src={logo}
              alt="D&D Lanka"
              className="w-full h-full mb-3 ml-3 object-cover filter brightness-110 drop-shadow-[0_15px_40px_rgba(0,0,0,0.9)]"
            />
          </div>
          <h1 className="text-5xl md:text-7xl font-light mb-6 tracking-tight uppercase">
            <span className="bg-gradient-to-b from-slate-100 via-slate-300 to-slate-400 bg-clip-text text-transparent font-bold">
              D & D{" "}
            </span>
            <span className="bg-gradient-to-b from-amber-200 via-amber-500 to-amber-700 bg-clip-text text-transparent font-black">
              LANKA
            </span>
            <span className="block text-sm md:text-lg tracking-[0.5em] font-medium text-amber-600/80 mt-2">
              GROUP OF COMPANIES
            </span>
          </h1>
          <p className="text-slate-400 max-w-2xl mx-auto font-light tracking-wide italic border-t border-b border-amber-900/30 py-4 inline-block">
            Excellence in Spices, Natural Products & High-End Apparel
          </p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-24">
        {/* About Us Narrative Section */}
        <section id="about" className="mb-32 scroll-mt-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div>
                <h2 className="text-amber-500 font-bold tracking-[0.2em] uppercase text-sm mb-4">
                  Our Heritage
                </h2>
                <h3 className="text-4xl md:text-5xl font-black text-white leading-tight">
                  A Legacy of Quality <br />
                  From the{" "}
                  <span className="text-amber-500">
                    Pearl of the Indian Ocean
                  </span>
                </h3>
              </div>
              <p className="text-slate-400 text-lg leading-relaxed font-light">
                Over Three Decades of Global Trade Excellence Founded in 1992
                and now steered by its second generation of leadership, D&D
                LANKA GROUP OF COMPANIES is a premier Sri Lankan commodity
                broker with 34 years of experience. We specialize in the
                high-volume import and export of global commodities, with a
                particular expertise in the apparel and textile sectors—ranging
                from technical sportswear to traditional attire. By blending
                foundational industry wisdom with modern trade strategies, we
                leverage our strategic global network to provide reliable,
                bulk-quantity solutions that connect world-class suppliers with
                international buyers.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="p-6 bg-blue-900/20 rounded-2xl border border-amber-900/20">
                  <div className="text-3xl font-black text-amber-500 mb-1">
                    34+
                  </div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">
                    Years of Trust
                  </div>
                </div>
                <div className="p-6 bg-blue-900/20 rounded-2xl border border-amber-900/20">
                  <div className="text-3xl font-black text-amber-500 mb-1">
                    20+
                  </div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">
                    Countries Served
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-amber-500/10 blur-3xl rounded-full"></div>
              <div className="relative bg-gradient-to-br from-blue-950 to-slate-900 border border-amber-500/20 p-8 rounded-[3rem] shadow-2xl">
                <p className="text-xl italic text-slate-300 leading-relaxed mb-6">
                  "Our mission is to deliver the authentic essence of Sri Lanka
                  to the world while empowering our local communities through
                  sustainable and ethical business practices."
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-[1px] bg-amber-500"></div>
                  <span className="text-amber-500 font-bold uppercase tracking-widest text-sm">
                    Corporate Vision
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section>
          {/* FOUNDERS SECTION */}
          <div className="pt-16 border-t border-amber-900/20 mb-32">
            <div className="text-center mb-16">
              <h2 className="text-amber-500 font-bold tracking-[0.2em] uppercase text-sm mb-4">Leadership</h2>
              <h3 className="text-4xl font-black text-white">The Visionaries Behind D&D Lanka group of Companies</h3>
            </div>

            <div className="grid md:grid-cols-2 gap-12">
              {founders.map((founder, idx) => (
                <div key={idx} className="group relative bg-gradient-to-br from-blue-950/40 to-slate-900/40 border border-amber-900/20 rounded-[2rem] overflow-hidden hover:border-amber-500/30 transition-all duration-500 flex flex-col md:flex-row items-stretch">
                  <div className="w-full md:w-2/5 aspect-[4/5] md:aspect-auto md:h-full overflow-hidden bg-slate-800/50">
                    {founder.image ? (
                      <img
                        src={founder.image}
                        alt={founder.name}
                        className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-900/20 to-slate-900/20">
                        <div className="text-amber-500/30">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" className="w-24 h-24">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                            <circle cx="12" cy="7" r="4" />
                          </svg>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="p-8 md:w-3/5 flex flex-col justify-center">
                    <h4 className="text-2xl font-bold text-white mb-1 group-hover:text-amber-500 transition-colors">
                      {founder.name}
                    </h4>
                    <p className="text-amber-600 font-bold text-xs uppercase tracking-widest mb-4">
                      {founder.title}
                    </p>
                    <p className="text-slate-400 text-sm leading-relaxed font-light mb-6">
                      {founder.description}
                    </p>

                    <a
                      href={founder.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-slate-500 hover:text-amber-500 transition-colors"
                    >
                      <Linkedin size={18} />
                      <span className="text-xs font-bold uppercase tracking-tighter">Professional Profile</span>
                    </a>
                  </div>
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center text-black">
                      <CheckCircle size={16} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
        <section>
          {/* Triangle Layout Grid */}
          <div id="companies" className="flex flex-col gap-8 mb-32">
            {topCompany && (
              <div className="flex justify-center">
                <button
                  onClick={() => onSelectCompany(topCompany)}
                  className="group relative p-12 bg-blue-950/20 border border-white/5 hover:border-amber-500/40 rounded-3xl text-center transition-all duration-700 overflow-hidden shadow-2xl max-w-2xl w-full flex flex-col items-center"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                  <div className="relative z-10 flex flex-col items-center">
                    <div className="w-28 h-28 bg-gradient-to-br from-blue-900 to-slate-900 border border-amber-600/30 flex items-center justify-center mb-8 group-hover:border-amber-400 transition-all duration-500 shadow-2xl overflow-hidden rounded-2xl">
                      {getCompanyIcon(topCompany.icon)}
                    </div>
                    <h2 className="text-3xl font-bold mb-4 uppercase tracking-tight text-white group-hover:text-amber-400 transition-colors">
                      {topCompany.name}
                    </h2>
                    <p className="text-slate-400 text-lg mb-8 leading-relaxed font-light group-hover:text-slate-200 transition-colors max-w-lg">
                      {topCompany.description}
                    </p>
                    <div className="flex items-center gap-3 font-bold text-xs uppercase tracking-[0.2em] text-amber-500 group-hover:text-amber-300 transition-all">
                      Discover Venture{" "}
                      <ArrowRight
                        size={16}
                        className="group-hover:translate-x-3 transition-transform duration-500"
                      />
                    </div>
                  </div>
                </button>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-8">
              {bottomCompanies.map((company) => (
                <button
                  key={company.id}
                  onClick={() => onSelectCompany(company)}
                  className="group relative p-12 bg-blue-950/20 border border-white/5 hover:border-amber-500/40 rounded-3xl text-center transition-all duration-700 overflow-hidden shadow-2xl flex flex-col items-center"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                  <div className="relative z-10 flex flex-col items-center">
                    <div className="w-28 h-28 bg-gradient-to-br from-blue-900 to-slate-900 border border-amber-600/30 flex items-center justify-center mb-8 group-hover:border-amber-400 transition-all duration-500 shadow-2xl overflow-hidden rounded-2xl">
                      {getCompanyIcon(company.icon)}
                    </div>
                    <h2 className="text-3xl font-bold mb-4 uppercase tracking-tight text-white group-hover:text-amber-400 transition-colors">
                      {company.name}
                    </h2>
                    <p className="text-slate-400 text-lg mb-8 leading-relaxed font-light group-hover:text-slate-200 transition-colors">
                      {company.description}
                    </p>
                    <div className="flex items-center gap-3 font-bold text-xs uppercase tracking-[0.2em] text-amber-500 group-hover:text-amber-300 transition-all">
                      Discover Venture{" "}
                      <ArrowRight
                        size={16}
                        className="group-hover:translate-x-3 transition-transform duration-500"
                      />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Certifications & Quality Grid */}
        <div className="grid lg:grid-cols-5 gap-6 mb-32">
          {[
            {
              icon: <Leaf size={28} />,
              title: "Authentic Source",
              desc: "Single-origin garden fresh harvests from highlands.",
            },
            {
              icon: <Award size={28} />,
              title: "ISO Certified",
              desc: "ISO 22000:2018 & HACCP compliant standards.",
            },
            {
              icon: <Globe size={28} />,
              title: "Global Network",
              desc: "Trusted presence in USA, Europe & Asia markets.",
            },
            {
              icon: <ShieldCheck size={28} />,
              title: "Premium Quality",
              desc: "Rigorous quality control at every stage.",
            },
            {
              icon: <ShieldCheck size={28} />,
              title: "Approved Lab Certificates",
              desc: "Essential Certificates Provided for Cargo Clearence.",
            },
          ].map((item, idx) => (
            <div
              key={idx}
              className="transition-all duration-700 bg-gradient-to-br from-blue-900/40 to-[#020617] p-8 rounded-3xl border border-amber-900/20 hover:border-amber-500/40 hover:shadow-2xl group relative overflow-hidden"
            >
              <div className="relative z-10">
                <div className="mb-6 flex justify-center text-amber-500">
                  {item.icon}
                </div>
                <h3 className="text-lg font-bold text-white mb-3 tracking-wide text-center uppercase group-hover:text-amber-400">
                  {item.title}
                </h3>
                <p className="text-slate-400 text-xs leading-relaxed font-light text-center group-hover:text-slate-200">
                  {item.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* Contact Section */}
      <section
        id="contact"
        className="relative py-24 border-t border-amber-900/20 bg-gradient-to-b from-[#020617] to-black scroll-mt-20"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-16">
            <div className="lg:col-span-5 space-y-12">
              <div>
                <h2 className="text-4xl font-black text-white mb-6">
                  Contact <span className="text-amber-500">Us</span>
                </h2>
                <p className="text-slate-400 text-lg">
                  Inquiries about bulk orders or partnerships? Our team is ready
                  to assist you.
                </p>
              </div>
              <div className="space-y-6">
                <div className="flex gap-6 items-start">
                  <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center text-amber-500 flex-shrink-0">
                    <Mail size={24} />
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">Email Us</h4>
                    <p className="text-slate-400 text-sm">
                      info@ddlankagroup.com
                    </p>
                  </div>
                </div>
                <div className="flex gap-6 items-start">
                  <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center text-amber-500 flex-shrink-0">
                    <Phone size={24} />
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">Direct Line</h4>
                    <a href="tel:+94778197763" className="text-slate-400 text-sm">+94 77 819 7763</a>
                  </div>
                </div>
                <div className="flex gap-6 items-start">
                  <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center text-amber-500 flex-shrink-0">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">Headquarters</h4>
                    <p className="text-slate-400 text-sm">Colombo, Sri Lanka</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="bg-blue-950/20 p-8 rounded-[2rem] border border-amber-500/10 backdrop-blur-xl">
                {formStatus === "success" ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-green-500/20 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle size={40} />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">
                      Message Sent!
                    </h3>
                    <p className="text-slate-400 mb-8">
                      We will get back to you shortly.
                    </p>
                    <button
                      onClick={() => setFormStatus("idle")}
                      className="text-amber-500 font-bold hover:underline"
                    >
                      Send another message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleFormSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-widest ml-1">
                          Full Name
                        </label>
                        <input
                          required
                          type="text"
                          className="w-full bg-black/40 border border-white/10 rounded-xl p-4 focus:border-amber-500 focus:outline-none text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-widest ml-1">
                          Email
                        </label>
                        <input
                          required
                          type="email"
                          className="w-full bg-black/40 border border-white/10 rounded-xl p-4 focus:border-amber-500 focus:outline-none text-white"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-widest ml-1">
                        Message
                      </label>
                      <textarea
                        required
                        rows="4"
                        className="w-full bg-black/40 border border-white/10 rounded-xl p-4 focus:border-amber-500 focus:outline-none text-white resize-none"
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      disabled={formStatus === "submitting"}
                      className="w-full py-5 bg-gradient-to-r from-amber-600 to-amber-700 text-white rounded-xl font-black text-lg shadow-xl flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-amber-500/20 hover:shadow-2xl transition-all"
                    >
                      {formStatus === "submitting" ? (
                        "Sending..."
                      ) : (
                        <>
                          Send Message <Send size={20} />
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PortalPage;